import { useCallback, useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';

/**
 * useWebSearchForBeer
 * Provides beer search and scraping functionality.
 *
 * @param {string} breweryName - Name of the brewery
 * @param {string} breweryCity - City of the brewery
 * @param {string} breweryState - State of the brewery
 * @returns {Object} Search and scraping state and handlers
 */
export function useWebSearchForBeer(breweryName, breweryCity, breweryState) {
  const [beerUrl, setBeerUrl] = useState('');
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingUrl, setScrapingUrl] = useState(null);
  const [scrapingError, setScrapingError] = useState(null);
  const [scrapingResult, setScrapingResult] = useState(null);
  const dialogOpenRef = useRef(false);

  // Keep ref in sync with dialog state
  useEffect(() => {
    // This will be set by the parent component
  }, []);

  const handleFindBeer = useCallback(async () => {
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults(null);
    setBeerUrl('');
    setScrapingResult(null);
    setScrapingError(null);

    // Build search query: breweryName + city + state + 'beer list'
    const searchParts = [];
    if (breweryName) searchParts.push(breweryName);
    if (breweryCity) searchParts.push(breweryCity);
    if (breweryState) searchParts.push(breweryState);
    searchParts.push('beer list');
    const searchQuery = searchParts.join(' ');

    try {
      const searchWebFn = httpsCallable(
        functions,
        'callableSearchSearchWeb-searchWeb',
      );
      const result = await searchWebFn({
        query: searchQuery,
        numResults: 10,
      });

      // Only update state if dialog is still open
      if (!dialogOpenRef.current) {
        return;
      }

      setSearchResults(result.data || {});
    } catch (err) {
      // Only update state if dialog is still open
      if (!dialogOpenRef.current) {
        return;
      }
      console.error('Error searching for beer list:', err);
      setSearchError(
        err?.message || 'An error occurred while searching for beer lists',
      );
    } finally {
      // Only update state if dialog is still open
      if (dialogOpenRef.current) {
        setSearchLoading(false);
      }
    }
  }, [breweryName, breweryCity, breweryState]);

  const handleNext = useCallback(async (url) => {
    const urlToUse = url || beerUrl;
    if (!urlToUse || !urlToUse.trim()) {
      return;
    }

    setScrapingLoading(true);
    setScrapingUrl(urlToUse.trim());
    setScrapingError(null);
    setScrapingResult(null);

    try {
      // Call Firebase Cloud Function to scrape the URL
      const findBeerFn = httpsCallable(
        functions,
        'callableBeerFindBeer-findBeer',
        { timeout: 600000 }, // 10 minutes timeout for large beer lists
      );
      const result = await findBeerFn({
        beerUrl: urlToUse.trim(),
      });

      // Only update state if dialog is still open
      if (!dialogOpenRef.current) {
        return;
      }

      // Transform the response to match the expected format
      // The cloud function returns the data directly in result.data
      setScrapingResult({
        json: result.data,
      });
    } catch (err) {
      // Only update state if dialog is still open
      if (!dialogOpenRef.current) {
        return;
      }
      console.error('Error scraping beer data:', err);
      setScrapingError(
        err?.message || 'An error occurred while scraping the URL',
      );
    } finally {
      // Only update state if dialog is still open
      if (dialogOpenRef.current) {
        setScrapingLoading(false);
        setScrapingUrl(null);
      }
    }
  }, [beerUrl]);

  const handleSearchResultSelect = useCallback(
    (url) => {
      setBeerUrl(url);
      setSelectedUrl(url);
      // Automatically proceed to scraping
      handleNext(url);
    },
    [handleNext],
  );

  const handleSelectClick = useCallback(
    (e) => {
      const url = e.currentTarget.getAttribute('data-url');
      if (url) {
        handleSearchResultSelect(url);
      }
    },
    [handleSearchResultSelect],
  );

  const handleLinkClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const reset = useCallback(() => {
    setBeerUrl('');
    setSelectedUrl(null);
    setSearchLoading(false);
    setSearchError(null);
    setSearchResults(null);
    setScrapingLoading(false);
    setScrapingUrl(null);
    setScrapingError(null);
    setScrapingResult(null);
  }, []);

  return {
    beerUrl,
    selectedUrl,
    searchLoading,
    searchError,
    searchResults,
    scrapingLoading,
    scrapingUrl,
    scrapingError,
    scrapingResult,
    dialogOpenRef,
    handleFindBeer,
    handleNext,
    handleSearchResultSelect,
    handleSelectClick,
    handleLinkClick,
    reset,
  };
}
